import { useMemo, useState } from 'react';
import { Check, Close, PlayArrowOutlined, Settings } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { DataGridPro, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { CrudFilters, getDefaultFilter, useInvalidate } from '@refinedev/core';
import { List, useDataGrid, DateField, EditButton, DeleteButton } from '@refinedev/mui';
import { useForm } from '@refinedev/react-hook-form';
import { Controller } from 'react-hook-form';

import useDeselectFeaturedPackages from '../../hooks/useDeselectFeaturedPackages';
import useSetFeaturedPackages from '../../hooks/useSetFeaturedPackages';

import { BinaryNameDialog } from './components/binary_name_dialog';
import { buildAllPackages, buildManyPackages, buildPackage } from './components/build_package';
import { Status } from './components/constants';

const DIALOGTYPES = {
  REBUILD: 'rebuild_all',
  DESELECT_FEATURED: 'remove_featured',
  FEATURED: 'featured'
};

const FlexPackagesList = () => {
  const invalidate = useInvalidate();
  const deselectFeaturedPackages = useDeselectFeaturedPackages();
  const setFeaturedPackages = useSetFeaturedPackages();
  const [isBinaryNameDialogOpen, setIsBinaryNameDialogOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(DIALOGTYPES.REBUILD);
  const [idsSelected, setIdsSelected] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set() });
  const [binaryName, setBinaryName] = useState('');
  const [currentId, setCurrentId] = useState('');

  const invalidateListQuery = () => {
    invalidate({
      resource: 'flex_packages',
      invalidates: ['list']
    });
  };

  const columns = useMemo<GridColDef[]>(() => {
    return [
      {
        field: 'package_image',
        headerName: 'Image',
        sortable: false,
        renderCell: params => {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px'
              }}
              component="picture"
            >
              <img
                style={{
                  width: '100%',
                  height: '100%'
                }}
                onError={e => {
                  e.target.remove();
                }}
                src={params.row.package_image}
                alt={`${params.row.name}image`}
              />
            </Box>
          );
        }
      },
      {
        field: 'name',
        headerName: 'Flex Package Name',
        width: 200,
        flex: 2,
        renderCell: params => {
          return (
            <Tooltip title={params.row.name}>
              <Typography variant="body1" noWrap>
                {params.row.name}
              </Typography>
            </Tooltip>
          );
        }
      },
      {
        field: 'package_type',
        headerName: 'Package Type',
        flex: 2,
        renderCell: params => {
          return (
            <Tooltip title={params.row.package_type_name}>
              <Typography variant="body1" noWrap>
                {params.row.package_type_name}
              </Typography>
            </Tooltip>
          );
        }
      },
      {
        field: 'featured',
        headerName: 'Featured',
        flex: 1,
        renderCell: params => (params.row.featured ? <Check color="success" /> : <Close color="error" />)
      },
      {
        field: 'version',
        headerName: 'Version',
        flex: 1,
        renderCell: params => {
          return (
            <Tooltip title={params.row.version}>
              <Typography variant="body1" noWrap>
                {params.row.version}
              </Typography>
            </Tooltip>
          );
        }
      },
      {
        field: 'executor_version',
        headerName: 'Executor version',
        flex: 1,
        renderCell: params => {
          return (
            <Tooltip title={params.row.executor_version}>
              <Typography variant="body1" noWrap>
                {params.row.executor_version}
              </Typography>
            </Tooltip>
          );
        }
      },
      {
        field: 'os',
        headerName: 'OS',
        flex: 1,
        renderCell: params => {
          return (
            <Tooltip title={params.row.os}>
              <Typography variant="body1" noWrap>
                {params.row.os}
              </Typography>
            </Tooltip>
          );
        }
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        renderCell: params => {
          return (
            <Tooltip title={params.row.status}>
              <Typography variant="body1" noWrap>
                {params.row.status}
              </Typography>
            </Tooltip>
          );
        }
      },
      {
        field: 'created',
        headerName: 'Created At',
        renderCell: ({ value }) => <DateField format="LLL" value={value} />,
        flex: 1
      },
      {
        field: 'modified',
        headerName: 'Updated At',
        renderCell: ({ value }) => <DateField format="LLL" value={value} />,
        flex: 1
      },
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 250,
        sortable: false,
        renderCell: params => {
          return (
            <Stack direction="row" spacing={1}>
              <EditButton hideText recordItemId={params.row.id} />
              <Button
                sx={{ minWidth: 0 }}
                startIcon={<Settings sx={{ selfAlign: 'center' }} />}
                title="Recreate Package with binary name settings"
                disabled={params.row.status === Status.IN_PROGRESS}
                onClick={() => {
                  setBinaryName(params.row.binary_name);
                  setCurrentId(params.row.id);
                  setIsBinaryNameDialogOpen(true);
                }}
              />
              <Button
                sx={{ minWidth: 0 }}
                startIcon={<PlayArrowOutlined sx={{ selfAlign: 'center' }} />}
                title="Recreate Package"
                disabled={params.row.status === Status.IN_PROGRESS}
                onClick={() => {
                  buildPackage({
                    id: params.row.id,
                    binaryName: params.row.binary_name,
                    onFinish: invalidateListQuery
                  });
                }}
              />
              <DeleteButton
                hideText
                recordItemId={params.row.id}
                confirmTitle={'Do you want to delete Flex Package: "' + params.row.name + '" ?'}
              />
            </Stack>
          );
        }
      }
    ];
  }, [invalidate]);

  const handleCloseBinaryNameDialog = async (success: boolean, binary_name: string) => {
    setIsBinaryNameDialogOpen(false);
    if (success) {
      buildPackage({ id: currentId, binaryName: binary_name, onFinish: invalidateListQuery });
    }
  };

  const rebuildSelected = async () => {
    buildManyPackages({ ids: Array.from(idsSelected.ids) as string[], onFinish: invalidateListQuery });
    setIdsSelected({ type: 'include', ids: new Set() });
  };

  const rebuildAll = async () => {
    setIsDialogOpen(false);
    buildAllPackages({ onFinish: invalidateListQuery });
  };

  const removeFeatured = async () => {
    setIsDialogOpen(false);
    await deselectFeaturedPackages({ onFinish: invalidateListQuery });
  };

  const featuredSelected = async () => {
    setIsDialogOpen(false);
    await setFeaturedPackages({ ids: Array.from(idsSelected.ids) as string[], onFinish: invalidateListQuery });
  };

  const { dataGridProps, filters, search } = useDataGrid({
    onSearch: (params: { search?: string; os?: string }) => {
      const filters: CrudFilters = [];
      const { search, os } = params;

      if (search) {
        filters.push({
          field: 'search',
          operator: 'eq',
          value: search
        });
      }

      if (os) {
        filters.push({
          field: 'os',
          operator: 'eq',
          value: os
        });
      }

      return filters;
    },

    filters: {
      initial: [
        {
          field: 'search',
          operator: 'eq',
          value: ''
        }
      ]
    }
  });

  const { control, handleSubmit } = useForm({
    defaultValues: {
      search: getDefaultFilter('search', filters, 'eq') || '',
      os: getDefaultFilter('os', filters, 'eq') || ''
    }
  });

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={3}>
          <Card>
            <CardHeader title="Filters" />
            <CardContent>
              <Box
                component="form"
                autoComplete="off"
                sx={{
                  display: 'flex',
                  flexDirection: 'column'
                }}
                onSubmit={handleSubmit(search)}
              >
                <Controller
                  name="search"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      value={value || ''}
                      onChange={onChange}
                      label="Flex Package Name"
                      placeholder="Search by name"
                      margin="normal"
                      fullWidth
                      autoFocus
                    />
                  )}
                />

                <br />
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                  Operating System
                </Typography>
                <Controller
                  name="os"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <RadioGroup value={value} onChange={onChange}>
                      <FormControlLabel value="" control={<Radio />} label="All" />
                      <FormControlLabel value="windows" control={<Radio />} label="Windows" />
                      <FormControlLabel value="linux" control={<Radio />} label="Linux" />
                      <FormControlLabel value="os x" control={<Radio />} label="OSX" />
                    </RadioGroup>
                  )}
                />
                <br />
                <Button type="submit" variant="contained">
                  Apply
                </Button>
              </Box>
            </CardContent>
          </Card>
          <br />
          <Card>
            <CardHeader title="Build options" />
            <CardContent>
              <Button onClick={rebuildSelected} disabled={idsSelected.ids.size == 0} variant="contained">
                Rebuild Selected
              </Button>
              <br />
              <br />
              <Button onClick={rebuildSelected} disabled={true} variant="contained">
                Rebuild Type (coming soon)
              </Button>
              <br />
              <br />
              <Button
                onClick={() => {
                  setDialogType(DIALOGTYPES.REBUILD);
                  setIsDialogOpen(true);
                }}
                variant="contained"
              >
                Rebuild All
              </Button>
            </CardContent>
          </Card>
          <br />
          <Card>
            <CardHeader title="Configuration options" />
            <CardContent>
              <Alert severity="info"> The options presented here will make modifications to the Flex Packages</Alert>
              <br />
              <Button
                onClick={() => {
                  setDialogType(DIALOGTYPES.DESELECT_FEATURED);
                  setIsDialogOpen(true);
                }}
                variant="contained"
              >
                Remove "featured" from packages
              </Button>
              <br />
              <br />
              <Button
                onClick={() => {
                  setDialogType(DIALOGTYPES.FEATURED);
                  setIsDialogOpen(true);
                }}
                variant="contained"
              >
                Add "featured" to selected packages
              </Button>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={9}>
          <List canCreate>
            <DataGridPro
              {...dataGridProps}
              checkboxSelection
              disableRowSelectionOnClick
              keepNonExistentRowsSelected
              filterModel={undefined}
              columns={columns}
              pagination
              rowSelectionModel={idsSelected}
              onRowSelectionModelChange={newRowSelectionModel => {
                setIdsSelected(newRowSelectionModel);
              }}
            />
          </List>
        </Grid>
      </Grid>
      <BinaryNameDialog open={isBinaryNameDialogOpen} onClose={handleCloseBinaryNameDialog} binaryName={binaryName} />
      <Dialog
        open={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
        }}
        fullWidth
      >
        {dialogType === DIALOGTYPES.DESELECT_FEATURED ? (
          <>
            <DialogTitle>Remove Featured from all</DialogTitle>
            <DialogContent>Are you sure?</DialogContent>
            <DialogActions>
              <Button onClick={removeFeatured}>Remove</Button>
            </DialogActions>
          </>
        ) : dialogType === DIALOGTYPES.REBUILD ? (
          <>
            <DialogTitle>Rebuild All</DialogTitle>
            <DialogContent>Are you sure?</DialogContent>
            <DialogActions>
              <Button onClick={rebuildAll}>Start Building</Button>
            </DialogActions>
          </>
        ) : dialogType === DIALOGTYPES.FEATURED ? (
          <>
            <DialogTitle>Add Featured to selected</DialogTitle>
            <DialogContent>Are you sure?</DialogContent>
            <DialogActions>
              <Button onClick={featuredSelected}>Add</Button>
            </DialogActions>
          </>
        ) : (
          <DialogTitle>Error</DialogTitle>
        )}
      </Dialog>
    </>
  );
};

export default FlexPackagesList;
